import { platformApprovedActionProviderDescriptorSchema, platformOptionalActionProviderHostCapability, } from '@crest/platform-contracts/hosts';
import { z } from 'zod';
import { tasksTaskPrioritySchema } from './tasks-task.contract.js';
import { tasksCancelCreatedTaskActionId } from './tasks-task-reversal-actions.contract.js';
export const tasksWorkflowActionsProviderId = 'tasks.workflow-actions';
export const tasksWorkflowActionsCapability = 'tasks.workflow-actions.v1';
export const tasksWorkflowActionsSlot = 'workflows.approved-owner-actions';
export const tasksWorkflowCreateTaskActionId = 'tasks.create-task.v1';
/**
 * Owner-declared legacy authored ids for this SAME action. Historical workflow
 * rule versions are immutable and some carry the earlier id; the owner accepts
 * them as aliases of the canonical action above and never registers a second
 * implementation. New authoring emits only the canonical id.
 */
export const tasksWorkflowCreateTaskLegacyActionIds = ['tasks.task-create.v1'];
export const tasksWorkflowCreateTaskAcceptedActionIds = [
    tasksWorkflowCreateTaskActionId,
    ...tasksWorkflowCreateTaskLegacyActionIds,
];
const uuid = z.string().uuid();
const schemaHash = z.string().regex(/^[a-f0-9]{64}$/);
/**
 * Rule-authored configuration for the workflow create-task action. Everything
 * here is fixed by the rule author; execution supplies only the target
 * Project identity on top of it.
 */
export const tasksWorkflowCreateTaskConfigurationSchema = z.strictObject({
    title: z.string().trim().min(1).max(240),
    description: z.string().trim().min(1).max(4000).nullable().default(null),
    priority: tasksTaskPrioritySchema.default('normal'),
    dueInDays: z.number().int().min(0).max(365).nullable().default(null),
});
/**
 * Full action input at execution time: the rule-authored configuration plus
 * the resolved target Project.
 */
export const tasksWorkflowCreateTaskInputSchema = z.strictObject({
    ...tasksWorkflowCreateTaskConfigurationSchema.shape,
    projectId: uuid,
});
/**
 * sha256 of the canonical JSON encoding of the create-task input schema's
 * JSON-Schema artifact; recomputed by the contract test.
 */
export const tasksWorkflowCreateTaskInputSchemaHash = '8ef1b089d791d1b4c478f4f7d53fb445daf077042e5ad3a49a5b4a19a07a0b7e';
export const tasksWorkflowActionsDescriptor = platformApprovedActionProviderDescriptorSchema.parse({
    kind: 'descriptor',
    contractVersion: platformOptionalActionProviderHostCapability,
    providerId: tasksWorkflowActionsProviderId,
    providerCapability: tasksWorkflowActionsCapability,
    displayName: 'Tasks workflow actions',
    actions: [{
            actionId: tasksWorkflowCreateTaskActionId,
            displayName: 'Create task',
            targetResourceType: 'projects.project',
            inputSchemaHash: tasksWorkflowCreateTaskInputSchemaHash,
            requiredPermission: 'tasks.tasks.create',
            assistantPolicy: {
                policyContractVersion: 'platform.assistant-action-policy.v1',
                confirmation: 'explicit',
                effectClass: 'compensating-write',
                externalEffectClass: 'none',
                reversal: {
                    kind: 'compensating-action',
                    registeredReversalActionId: tasksCancelCreatedTaskActionId,
                },
                ownerReadbackRequired: true,
                inputSchemaHash: tasksWorkflowCreateTaskInputSchemaHash,
            },
        }],
});
/**
 * Tasks-owned execution request for one pending workflow intent. The
 * commandId is the stable, per-intent idempotent Tasks create command, and
 * intentCreatedAt is the intent's immutable creation instant: every derived
 * value (the due date) is computed from it, never from execution time, so a
 * replayed execution submits the byte-identical command payload and returns
 * the original task instead of creating another.
 */
export const tasksWorkflowExecuteRequestSchema = z.strictObject({
    kind: z.literal('execute-workflow-intent'),
    contractVersion: z.literal(tasksWorkflowActionsCapability),
    executionId: uuid,
    intentId: uuid,
    runId: uuid,
    ruleId: uuid,
    ruleVersionId: uuid,
    sourceEventId: uuid,
    commandId: uuid,
    intentCreatedAt: z.string().datetime({ offset: true }),
    actionId: z.literal(tasksWorkflowCreateTaskActionId),
    // The id the rule author actually wrote (canonical or a declared legacy
    // alias). Evidence preserves it; execution resolves to the canonical action.
    authoredActionId: z.enum(tasksWorkflowCreateTaskAcceptedActionIds),
    inputSchemaHash: schemaHash,
    input: tasksWorkflowCreateTaskInputSchema,
});
export const tasksWorkflowExecuteResultSchema = z.discriminatedUnion('outcome', [
    z.strictObject({
        outcome: z.literal('completed'),
        contractVersion: z.literal(tasksWorkflowActionsCapability),
        readback: z.strictObject({
            subjectType: z.literal('tasks.task'),
            taskId: uuid,
            projectId: uuid,
            status: z.string().min(1).max(40),
            title: z.string().min(1).max(240),
            replayed: z.boolean(),
        }),
    }),
    z.strictObject({
        outcome: z.literal('failed'),
        contractVersion: z.literal(tasksWorkflowActionsCapability),
        failure: z.strictObject({
            code: z.enum([
                'input-invalid',
                'schema-mismatch',
                'permission-denied',
                'target-unavailable',
                'action-failed',
            ]),
            message: z.string().trim().min(1).max(500),
            retryable: z.boolean(),
        }),
    }),
]);
export const tasksWorkflowActionsRequestSchema = z.discriminatedUnion('kind', [
    z.strictObject({
        kind: z.literal('describe'),
        contractVersion: z.literal(platformOptionalActionProviderHostCapability),
    }),
    tasksWorkflowExecuteRequestSchema,
]);
//# sourceMappingURL=tasks-workflow-actions.contract.js.map